import * as React from 'react';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment, GdsServiceProductStatus } from '@websky/graphql';
import { ServiceOptionItem } from '../../../ServiceSummary/types';
import { Props as CardExtraProps } from '../CardExtra';
import { ThemeProvider, useTheme } from '../../../theme';
import { getServiceSummaryType } from '../../utils';
import { useIsMobile } from '../../../utils';
import CardExtraContent from '../CardExtraContent/CardExtraContent';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import CartItemOptionAdapter from '../../../Cart/CartItemOptionAdapter/CartItemOptionAdapter';
import { ModalX } from '../../../BaseComponents';
import { ServiceSummary } from '../../../features/service-summary';

import { useCardExtraSummaryDelete } from '../CardExtraSummary/hooks';
import { useCardExtraCtx } from '../CardExtra.context';
import CardExtra from '../CardExtra';
import { useCardExtra } from '../../hooks';

const CardExtraGroup: React.FC<Pick<CardExtraProps, 'groupServices'>> = ({ groupServices }) => {
	const { t } = useTranslation('CardExtra');
	const { CardExtraGroup: theme } = useTheme('CardExtra');

	const isMobile = useIsMobile();

	const { type, filter, variant, segmentId, additionalServicesByType } = useCardExtraCtx();
	const model = useCardExtra({ type, filter, variant });

	const onDelete = useCardExtraSummaryDelete();

	const { icon, title, description, style } = useMemo(() => {
		switch (type) {
			case 'misc':
				const miscService = additionalServicesByType.gdsServices
					?.services?.[0] as GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

				const miscServiceGroupInfo = miscService.subtype;

				if (miscServiceGroupInfo) {
					return {
						icon: miscServiceGroupInfo.icon,
						title: miscServiceGroupInfo.title,
						description: miscServiceGroupInfo.shortDescription,
						style: {
							headerBg: miscServiceGroupInfo.backgroundColor,
							displayOrder: miscService.displayOrder
						}
					};
				}

				return {
					icon: miscService.icon,
					title: miscService.name,
					description: miscService.description,
					style: {
						headerBg: miscService.backgroundColor,
						displayOrder: miscService.displayOrder
					}
				};
			default:
				const service = additionalServicesByType.gdsServices?.services?.[0];

				return { title: service.name, description: service.description, displayOrder: service.displayOrder };
		}
	}, [type, additionalServicesByType]);

	return (
		<ThemeProvider
			value={{
				CardExtra: {
					CardExtraContent: {
						root: theme.card,
						root_aggregator: theme.card_aggregator
					}
				},
				BaseComponents: {
					ModalX: {
						paper_paperScrollBody: theme.modal__paper,
						top_wrapper: theme.modal__topWrapper
					}
				}
			}}
		>
			<ServiceSummary
				onDelete={onDelete}
				serviceType={getServiceSummaryType(type)}
				filter={filter}
				segmentId={segmentId}
				renderSummary={groups => {
					const isSelected = groups.length > 0;
					const itemsByStatus = new Map<GdsServiceProductStatus, Map<string, ServiceOptionItem>>();

					groups.forEach(group => {
						group.items.forEach(item => {
							const name = item.name;
							const status = item.status;

							const itemsByName = itemsByStatus.get(status) ?? new Map<string, ServiceOptionItem>();
							const existItem = itemsByName.get(name);

							const price = {
								...item.price,
								amount: (item.price?.amount || 0) + (existItem?.price?.amount || 0)
							};

							const onDelete = () => {
								existItem?.onDelete();
								item.onDelete?.();
							};

							itemsByName.set(name, { ...item, price, onDelete });
							itemsByStatus.set(status, itemsByName);
						});
					});

					return (
						<ThemeProvider
							value={{
								ServiceSummary: {
									ServiceSummaryOption: {
										option__name: theme.summaryOption__name,
										option__money: theme.summaryOption__money
									}
								}
							}}
						>
							<CardExtraContent
								type={type}
								variant={variant}
								icon={icon}
								title={title}
								description={description}
								style={style}
								content={
									<ServiceSummaryDivider>
										{[...itemsByStatus.entries()].map(([status, itemsByName]) => (
											<Fragment key={status}>
												{[...itemsByName.entries()].map(([name, item]) => (
													<CartItemOptionAdapter
														key={name}
														option={{
															headerType: 'serviceName',
															items: [item]
														}}
													/>
												))}
											</Fragment>
										))}
									</ServiceSummaryDivider>
								}
								onChangeService={model.cardExtraDisclosure.onOpenModal}
								isSelected={isSelected}
							/>
						</ThemeProvider>
					);
				}}
			/>

			<ModalX
				title={<div className={theme.title}>{t('ModalGroupTitle')}</div>}
				size="large"
				scroll={isMobile ? 'paper' : 'body'}
				isOpen={model.cardExtraDisclosure.isOpenModal}
				onClose={model.cardExtraDisclosure.onCloseModal}
				keepMounted={false}
			>
				<div className={theme.root}>
					{groupServices.map((value, index) => (
						<CardExtra key={`${type}-${index}`} type={type} variant={variant} filter={value.filter} />
					))}
				</div>
			</ModalX>
		</ThemeProvider>
	);
};

export default CardExtraGroup;
